import { useEffect, useMemo, useState } from "react";
import { Button, Dialog } from "@dfws/ui";
import { useFilterPreview, useMailboxes, useSaveFilter, type Filter, type FilterActions, type FilterCriteria } from "../../lib/queries";
import { ApiFailure } from "../../lib/api";
import { listDate } from "../mail/format";
import { Notice } from "./parts";
import s from "./Settings.module.css";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const clean = (c: FilterCriteria): FilterCriteria =>
  Object.fromEntries(Object.entries(c).filter(([, v]) => v !== "" && v !== undefined && v !== false && !(typeof v === "number" && Number.isNaN(v)))) as FilterCriteria;

export function FilterEditor({ initial, onClose }: { initial: Partial<Filter> | null; onClose: () => void }) {
  const [c, setC] = useState<FilterCriteria>(initial?.criteria ?? {});
  const [a, setA] = useState<FilterActions>(initial?.actions ?? {});
  const save = useSaveFilter();
  const boxes = useMailboxes();
  const criteria = useMemo(() => clean(c), [c]);
  const debounced = useDebounced(criteria, 350);
  const hasCriteria = Object.keys(debounced).length > 0;
  const preview = useFilterPreview(debounced, hasCriteria);
  const folders = (boxes.data?.items ?? []).filter((m) => m.role === "other" || m.role === "archive");

  const set = (k: keyof FilterCriteria) => (e: React.ChangeEvent<HTMLInputElement>) => setC((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? (e.target.value ? Number(e.target.value) : undefined) : e.target.value }));
  const act = (k: keyof FilterActions) => (e: React.ChangeEvent<HTMLInputElement>) => setA((p) => ({ ...p, [k]: e.target.checked }));

  async function onSave() {
    const actions: FilterActions = Object.fromEntries(Object.entries(a).filter(([, v]) => v !== "" && v !== false && v !== undefined));
    try {
      const r = await save.mutateAsync({ id: initial?.id, enabled: initial?.enabled ?? true, criteria, actions });
      if (!r.warning) onClose();
    } catch {
      /* shown below */
    }
  }

  const error = save.error instanceof ApiFailure ? save.error.message : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={initial?.id ? "Edit filter" : "Create a filter"} size="lg">
      <div className={s.stack}>
        <div className={s.grid2}>
          <label className={s.field}><span>From</span><input className={s.input} value={c.from ?? ""} onChange={set("from")} placeholder="name@example.com" autoFocus /></label>
          <label className={s.field}><span>To <small>(also matches Cc)</small></span><input className={s.input} value={c.to ?? ""} onChange={set("to")} /></label>
          <label className={s.field}><span>Subject</span><input className={s.input} value={c.subject ?? ""} onChange={set("subject")} /></label>
          <label className={s.field}><span>Has the words</span><input className={s.input} value={c.hasWords ?? ""} onChange={set("hasWords")} /></label>
          <label className={s.field}><span>Does not have</span><input className={s.input} value={c.doesNotHave ?? ""} onChange={set("doesNotHave")} /></label>
          <label className={s.field}><span>Larger than <small>(MB)</small></span><input className={s.input} type="number" min={1} max={100} value={c.sizeOverMb ?? ""} onChange={set("sizeOverMb")} /></label>
        </div>
        <label className={s.check}><input type="checkbox" checked={Boolean(c.hasAttachment)} onChange={set("hasAttachment")} /> Has attachment</label>
        <p className={s.muted}>Separate several values with commas or OR. Matching ignores upper and lower case.</p>

        <div className={s.notice} style={{ background: "var(--color-surface-sunken)" }} aria-live="polite">
          {!hasCriteria ? (
            "Add a condition to see which messages it matches."
          ) : preview.data ? (
            <>
              <strong>{preview.data.total === 0 ? "No recent messages match" : `${preview.data.total.toLocaleString("en-GB")} recent message${preview.data.total === 1 ? "" : "s"} match`}</strong>
              {preview.data.approximate && " (word conditions are checked by the mail server and not counted here)"}
              {preview.data.samples.length > 0 && (
                <ul style={{ margin: "var(--space-2) 0 0", paddingLeft: "1.1em" }}>
                  {preview.data.samples.map((x, i) => (
                    <li key={i} dir="auto">
                      {x.from}: {x.subject || "(no subject)"} <span className={s.muted}>{listDate(new Date(x.date))}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            "Checking your mail..."
          )}
        </div>

        <h3 className={s.groupTitle}>When a message matches</h3>
        <div className={s.grid2}>
          <div>
            <label className={s.check}><input type="checkbox" checked={Boolean(a.skipInbox)} onChange={act("skipInbox")} /> Skip the Inbox (archive it)</label>
            <label className={s.check}><input type="checkbox" checked={Boolean(a.markRead)} onChange={act("markRead")} /> Mark as read</label>
            <label className={s.check}><input type="checkbox" checked={Boolean(a.star)} onChange={act("star")} /> Star it</label>
            <label className={s.check}><input type="checkbox" checked={Boolean(a.delete)} onChange={act("delete")} /> Delete it</label>
            <label className={s.check}><input type="checkbox" checked={Boolean(a.spam)} onChange={act("spam")} /> Send to Spam</label>
          </div>
          <div className={s.stack}>
            <label className={s.field}>
              <span>Move to folder</span>
              <select className={s.select} value={a.moveTo ?? ""} onChange={(e) => setA((p) => ({ ...p, moveTo: e.target.value || undefined }))}>
                <option value="">Do not move</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.path}>{f.role === "archive" ? "Archive" : f.name}</option>
                ))}
              </select>
            </label>
            <label className={s.field}>
              <span>Forward a copy to</span>
              <input className={s.input} type="email" value={a.forwardTo ?? ""} onChange={(e) => setA((p) => ({ ...p, forwardTo: e.target.value || undefined }))} placeholder="colleague@devfusion.tech" />
            </label>
          </div>
        </div>
        {error && <Notice kind="err">{error}</Notice>}
        {save.data?.warning && <Notice kind="warn">{save.data.warning}</Notice>}
        <p className={s.muted}>New filters apply to mail that arrives from now on.</p>
        <div className={s.actionsBar}>
          <Button variant="primary" onClick={onSave} loading={save.isPending}>{initial?.id ? "Save filter" : "Create filter"}</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Dialog>
  );
}
